/**
 * The saved view's entries as chips the alert sheet shows, and can remove.
 *
 * Pure, and deliberately React-free. The sheet has no page's filter fields,
 * so a chip is worded from the parameter itself: `totalVolume__gte=1000`
 * reads "Total volume at least 1,000".
 */

import { alertViewEntries, searchText, type AlertSearch } from './alert-view.ts'
import type { AlertReportParams, AlertReportWords } from './alert-rules-api.ts'
import { LIST_SEPARATOR, RANGE_SEPARATOR } from './column-filter.ts'
import { formatCount } from './format.ts'
import { merchantsById, type Merchant } from './merchants.ts'

/** One entry the sheet shows, and removes, as a chip. */
export interface AlertChip {
  param: string
  slug: string
  label: string
}

/** The operators that order a value, so a number in them reads with commas. */
const ORDERING: ReadonlySet<string> = new Set([
  'gte',
  'gt',
  'lte',
  'lt',
  'range'
])

/**
 * A field name split into lower-case words.
 *
 * @param name - A camelCase or snake_case name.
 * @returns The words.
 */
function nameParts(name: string): string[] {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[\s_.]+/)
    .filter(Boolean)
    .map(part => part.toLowerCase())
}

/**
 * A field name as words, for example `totalVolume` as "Total volume".
 *
 * @param name - The field or parameter name.
 * @returns The words, first one capitalised.
 */
export function fieldWords(
  name: string,
  words: Pick<AlertReportWords, 'labels'>
): string {
  const label = words.labels[name] ?? name
  return label.charAt(0).toUpperCase() + label.slice(1)
}

/**
 * A field name as a `data-testid` segment, for example `total-volume`.
 *
 * @param name - The field or parameter name.
 * @returns The kebab-case slug.
 */
function slugOf(name: string): string {
  return nameParts(name).join('-')
}

/**
 * One filter value as words.
 *
 * @param op - The operator.
 * @param value - The value in the URL.
 * @returns The words.
 */
function valueWords(op: string, value: string): string {
  const one = (part: string): string =>
    ORDERING.has(op) && part.trim() !== '' && Number.isFinite(Number(part))
      ? formatCount(Number(part))
      : part
  if (op === 'range') {
    const [from = '', to = ''] = value.split(RANGE_SEPARATOR)

    return `${one(from)} and ${one(to)}`
  }
  if (op === 'in') {
    return value.split(LIST_SEPARATOR).join(', ')
  }

  return one(value)
}

/**
 * A `field__operator` entry as words.
 *
 * @param param - The parameter.
 * @param value - Its value.
 * @returns The chip's words.
 */
function filterWords(
  param: string,
  value: string,
  words: AlertReportWords
): string {
  const cut = param.lastIndexOf('__')
  const field = fieldWords(cut === -1 ? param : param.slice(0, cut), words)
  const op = cut === -1 ? 'exact' : param.slice(cut + 2)
  if (op === 'isnull') {
    return `${field} ${value === 'false' ? 'is not empty' : 'is empty'}`
  }
  if (value === 'true' || value === 'false') {
    return `${field}: ${value === 'true' ? 'Yes' : 'No'}`
  }

  return `${field} ${words.operators[op] ?? op} ${valueWords(op, value)}`
}

/**
 * A sort as words, for example "Sorted by Total volume, descending".
 *
 * @param value - The `sort` parameter.
 * @returns The chip's words.
 */
function sortWords(value: string, words: AlertReportWords): string {
  const keys = value
    .split(',')
    .filter(Boolean)
    .map(key =>
      key.startsWith('-')
        ? `${fieldWords(key.slice(1), words)}, descending`
        : `${fieldWords(key, words)}, ascending`
    )

  return `Sorted by ${keys.join('; ')}`
}

/**
 * A merchant scope entry, with each id named through the merchant list.
 *
 * @param param - `orgIds`, `orgId` or `merchantId`.
 * @param value - One id, or a comma-separated list for `orgIds`.
 * @param merchants - The merchant list.
 * @returns The chip, or null when the parameter names no merchant.
 */
function merchantChip(
  param: string,
  value: string,
  merchants: readonly Merchant[],
  words: AlertReportWords
): AlertChip | null {
  if (words.scope?.param !== param) {
    return null
  }
  const ids = value
    .split(',')
    .map(id => id.trim())
    .filter(Boolean)
  const byId = merchantsById(merchants)
  const names = ids.map(id => byId.get(Number(id))?.orgName || id)
  // A hand-edited `orgIds=,` names nobody, and the view still saves it.
  const named = names.length > 0 ? names.join(', ') : value

  return {
    param,
    slug: slugOf(words.scope.some),
    label: `${fieldWords(words.scope.some, { labels: {} })}: ${named}`
  }
}

/**
 * The saved view's entries as chips: the scope, then the filters, then the sort.
 *
 * A scope switch the catalogue lists shows even when the URL leaves it at its
 * default, because the default still narrows the rows.
 *
 * @param search - The page's URL search.
 * @param params - What the report's saved view may carry.
 * @param merchants - Names for the merchant ids.
 * @param dropped - Parameters the reader removed.
 * @returns The chips.
 */
export function alertChips(
  search: AlertSearch,
  params: AlertReportParams,
  merchants: readonly Merchant[],
  dropped: readonly string[] = [],
  words: AlertReportWords = { labels: {}, operators: {}, search: 'search' }
): AlertChip[] {
  const entries = alertViewEntries(search, params).filter(
    entry => !dropped.includes(entry.param)
  )
  const scope: AlertChip[] = []
  for (const param of params.scope) {
    if (dropped.includes(param)) {
      continue
    }
    const switchWords = words.switches?.[param]
    if (switchWords) {
      scope.push({
        param,
        slug: slugOf(param),
        label:
          searchText(search[param]) === 'false'
            ? switchWords.false
            : switchWords.true
      })
      continue
    }
    const entry = entries.find(item => item.param === param)
    if (!entry) {
      continue
    }
    scope.push(
      merchantChip(param, entry.value, merchants, words) ?? {
        param,
        slug: slugOf(param),
        label: `${fieldWords(param, words)}: ${entry.value}`
      }
    )
  }
  const filters = entries
    .filter(entry => entry.kind === 'filter')
    .map(entry => {
      const cut = entry.param.lastIndexOf('__')

      return {
        param: entry.param,
        slug:
          cut === -1
            ? slugOf(entry.param)
            : `${slugOf(entry.param.slice(0, cut))}-${entry.param.slice(cut + 2)}`,
        label: filterWords(entry.param, entry.value, words)
      }
    })
  const sort = entries
    .filter(entry => entry.kind === 'sort')
    .map(entry => ({
      param: entry.param,
      slug: 'sort',
      label: sortWords(entry.value, words)
    }))

  const searchChips = entries
    .filter(entry => entry.kind === 'search')
    .map(entry => ({
      param: entry.param,
      slug: 'search',
      label: `${fieldWords('search', { labels: { search: words.search } })}: ${entry.value}`
    }))

  return [...scope, ...filters, ...sort, ...searchChips]
}
